// @flow
import * as React from 'react';
import {CodeTabGroup, CodeBlock, CodeTab} from 'docs/components';
import {ContentGroupItem} from 'docs/util';
import {renderMarkdocAsReact} from 'docs/markdoc';
import * as Sentry from '@sentry/react';
import {useAccount} from 'docs/data';
import {formatLanguage} from 'docs/util/formatLanguage';
import type {Tag} from '@markdoc/markdoc';
import {
  addTooltipsToGeneratedCode,
  getCodeBlockHighlightRange,
} from './addTooltipsToGeneratedCode';

type GeneratedCodeBlockSuccess = {|
  lang: string,
  codegenError: null,
  generatedCode: string,
  overriddenCodeBlock: null,
|};

type GeneratedCodeBlockError = {|
  lang: string,
  codegenError: string,
  generatedCode: null,
  overriddenCodeBlock: null,
|};

type OverridenCodeBlock = {|
  lang: string,
  codegenError: null,
  generatedCode: null,
  overriddenCodeBlock: Array<string | Tag>,
|};

export type CodeSnippetGeneratedCodeBlock =
  | GeneratedCodeBlockError
  | GeneratedCodeBlockSuccess
  | OverridenCodeBlock;

type CodeSnippetGeneratedProps = {|
  generatedCodeBlocks: Array<CodeSnippetGeneratedCodeBlock>,
  // This ID is used for analytics
  codeTabGroupID: string,
  argsToHighlight?: Array<string>,
|};

export const CodegenSnippet = ({
  generatedCodeBlocks,
  codeTabGroupID,
  argsToHighlight,
}: CodeSnippetGeneratedProps) => {
  const {account} = useAccount();

  const items = generatedCodeBlocks
    .map(({lang, codegenError, overriddenCodeBlock, generatedCode}) => {
      if (codegenError) {
        if (Sentry && Sentry.captureMessage) {
          Sentry.captureMessage(
            `Failed to generate code for ${lang}. Hiding this from the user.`,
            codegenError,
          );
        }

        if (process.env.NODE_ENV === 'development') {
          return new ContentGroupItem(
            lang,
            'Error',
            (
              <pre style={{color: '#ff7171', padding: '40px'}}>
                {`(This is only shown in development)\n${codegenError}`}
              </pre>
            ),
          );
        }

        return null;
      }

      const title =
        lang === 'cli' ? 'Stripe CLI' : formatLanguage.fromPrismValue(lang);

      const code = overriddenCodeBlock
        ? overriddenCodeBlock.map(renderMarkdocAsReact)
        : addTooltipsToGeneratedCode(
            generatedCode,
            lang,
            account.keys?.secret,
            account.merchant?.apiVersion || account.latestApiVersion,
          );

      return new ContentGroupItem(
        lang,
        title,
        (
          <CodeTab key={lang} id={lang} title={title}>
            <CodeBlock
              language={lang}
              highlights={
                getCodeBlockHighlightRange(argsToHighlight, generatedCode) ||
                undefined
              }
            >
              {code}
            </CodeBlock>
          </CodeTab>
        ),
      );
    })
    .filter(Boolean);

  if (items.length < 1) {
    // Hide the code tab if code gen did not work.
    return null;
  }

  return <CodeTabGroup items={items} codeTabGroupID={codeTabGroupID} />;
};
